import { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import { Bug, ExternalLink, Plus, X } from 'lucide-react'
import { useAuth } from '@/contexts/AuthContext'
import Header from '../../../../components/Header'
import { MyIssuesList } from '../reporter/MyIssuesList'
import { NewIssueForm } from '../reporter/NewIssueForm'
import { IssueThread } from '../thread/IssueThread'
import { IssueLoadState } from '../thread/IssueLoadState'
import { useIssue } from '../../hooks/useSystemIssues'
import { useSystemIssuesSocket } from '../../hooks/useSystemIssuesSocket'
import { SYSTEM_ISSUES_NOT_ENABLED_MESSAGE } from '../../utils/access'

type View = 'list' | 'new' | { issueId: number }

interface SystemIssuesLayoutProps {
  /**
   * 'widget' = dentro do painel flutuante (mostra link "abrir em tela
   * cheia" e botão de fechar). 'page' = rota dedicada `/system-issues`.
   */
  variant: 'widget' | 'page'
  relatedProcessId?: number | null
  initialIssueId?: number | null
  onClose?: () => void
  /** Só dispara pra 'list' (null) e para a thread selecionada — 'new' não tem URL própria. */
  onViewChange?: (issueId: number | null) => void
}

/**
 * Experiência do lado do reporter: lista "meus problemas" + formulário de
 * novo problema + thread individual. Usado tanto pelo drawer flutuante
 * quanto pela rota `/system-issues`. A fila de triagem (dev) é uma tela
 * separada — ver `TriagePage`.
 */
export function SystemIssuesLayout({
  variant,
  relatedProcessId,
  initialIssueId,
  onClose,
  onViewChange,
}: SystemIssuesLayoutProps) {
  const [view, setView] = useState<View>(
    initialIssueId ? { issueId: initialIssueId } : 'list',
  )
  const selectedIssueId = typeof view === 'object' ? view.issueId : null
  // O drawer abre para qualquer usuário (item "Relatar um problema" da
  // Sidebar, sem gate de propósito), mas tudo aqui exige "Reportar problemas
  // do Hub" — sem ela, explica o que falta em vez de abrir lista e formulário
  // que o backend recusa (403). A rota `/system-issues` já é gated.
  const blocked = !useAuth().hasCap('can_report_system_issues')
  useSystemIssuesSocket({ issueId: selectedIssueId })
  const { data: selectedIssue, isError: issueFailed } = useIssue(selectedIssueId)

  useEffect(() => {
    if (view === 'new') return
    onViewChange?.(selectedIssueId)
  }, [view])

  return (
    <div className="flex h-full min-h-0 flex-col bg-surface-muted">
      {/* No modo 'page' o topbar padrão (Header) já mostra o título — o
          mini-header abaixo é só do drawer flutuante (link "tela cheia" +
          fechar), então só faz sentido no modo 'widget'. */}
      {variant === 'page' ? (
        <Header
          title="Reportar Problema"
          subtitle="Avise os desenvolvedores sobre um problema do sistema e acompanhe suas solicitações"
        />
      ) : (
        <div className="flex items-center justify-between border-b border-border-subtle bg-surface px-4 py-3">
          <h1 className="text-sm font-semibold text-body">Reportar Problema</h1>
          <div className="flex items-center gap-2">
            <Link
              to="/system-issues"
              className="flex items-center gap-1 text-xs text-muted hover:text-body"
              title="Abrir em tela cheia"
            >
              <ExternalLink className="h-3.5 w-3.5" />
            </Link>
            {onClose && (
              <button
                type="button"
                onClick={onClose}
                aria-label="Fechar"
                className="text-muted hover:text-body"
              >
                <X className="h-4 w-4" />
              </button>
            )}
          </div>
        </div>
      )}

      <div className="min-h-0 flex-1 overflow-hidden">
        {blocked && (
          <div className="flex h-full flex-col items-center justify-center gap-3 p-8 text-center">
            <Bug className="h-10 w-10 text-subtle" />
            <h2 className="text-sm font-semibold text-body">
              Atendimento HUB não liberado
            </h2>
            <p className="text-xs leading-relaxed text-muted">
              {SYSTEM_ISSUES_NOT_ENABLED_MESSAGE}
            </p>
          </div>
        )}

        {!blocked && view === 'list' && (
          <div className="flex h-full flex-col">
            <div className="border-b border-border-subtle p-3">
              <button
                type="button"
                onClick={() => setView('new')}
                className="flex w-full items-center justify-center gap-1.5 rounded-md bg-blue-600 px-3 py-2 text-xs font-medium text-white transition hover:bg-blue-700"
              >
                <Plus className="h-3.5 w-3.5" />
                Novo problema
              </button>
            </div>
            <div className="flex-1 overflow-y-auto">
              <MyIssuesList onSelect={(id) => setView({ issueId: id })} />
            </div>
          </div>
        )}

        {!blocked && view === 'new' && (
          <div className="h-full overflow-y-auto">
            <NewIssueForm
              onBack={() => setView('list')}
              relatedProcessId={relatedProcessId}
              onCreated={(issue) => setView({ issueId: issue.id })}
            />
          </div>
        )}

        {!blocked &&
          typeof view === 'object' &&
          (selectedIssue ? (
            <IssueThread
              issue={selectedIssue}
              onBack={() => setView('list')}
              canManage={false}
              compact
            />
          ) : (
            <IssueLoadState failed={issueFailed} onBack={() => setView('list')} />
          ))}
      </div>
    </div>
  )
}
